import { lazy, Suspense } from "react";
import { Center, Loader } from "@mantine/core";
import { JwtProvider } from "@funded-here-interface/common/src/providers/JwtProvider";
import { SessionInterceptorProvider } from "@funded-here-interface/common/src/providers/SessionInterceptorProvider";
import { setToken } from "@funded-here-interface/common/src/features/auth/authSlice_Admin";
import { adminResetActions } from "./Utils/adminResetActions";
import "./App.css";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "react-query";
import { store, persistor, RootState } from "./store";
import { Provider, useSelector } from "react-redux";
import { client } from "./reactQuery";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AcknowledgementLayout = lazy(
  () =>
    import("@funded-here-interface/common/src/Layouts/AcknowledgementLayout/AcknowledgementLayout"),
);
const LoginLayout = lazy(
  () =>
    import("@funded-here-interface/common/src/Layouts/LoginLayout/LoginLayout"),
);
const PasswordReset = lazy(
  () =>
    import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordReset"),
);
const PasswordResetMsg = lazy(
  () =>
    import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordResetMsg"),
);
const Navbar = lazy(
  () =>
    import("@funded-here-interface/common/src/components/MobileNavbar/Navbar"),
);
const TwoFAQRPageCommon = lazy(
  () => import("@funded-here-interface/common/src/pages/TwoFA/TwoFAQRCode"),
);
const OperationAndRiskLayout = lazy(
  () => import("./layouts/OperationAndRisk/OperationAndRiskLayout"),
);
const ProductLayout = lazy(
  () => import("./layouts/ProductLayout/ProductLayout"),
);
const SalesSupportLayout = lazy(
  () => import("./layouts/SalesSupportLayout/SalesSupportLayout"),
);

const StructuredNotePage = lazy(
  () => import("./screens/StructuredNote/StructuredNote"),
);
const OrgDetailsPage = lazy(
  () => import("./screens/OrgDetails/OrgDetailsPage"),
);
const Issusingspv = lazy(() => import("./screens/IssusingSPV.tsx/Issusingspv"));
const Dashboard = lazy(() => import("./screens/PMDashboard/DashBoard"));
const CampaignList = lazy(() => import("./screens/CampaignList/CampaignList"));
const CreateCampaign = lazy(
  () => import("./screens/CreateCampaign/CreateCampaign"),
);
const Login = lazy(() => import("./screens/Login/Login"));
const Notelist = lazy(() => import("./screens/Notelist/Notelist"));
const PmVerifySkuLoans = lazy(
  () => import("./screens/PmVerifySkuLoans/PmVerifySkuLoans"),
);
const IndividualList = lazy(
  () => import("./screens/IndividualList/IndividualList"),
);
const InvestorSummary = lazy(
  () => import("./screens/PMDashboard/Components/Investor/InvestorSummary"),
);
const Merchant = lazy(
  () => import("./screens/PMDashboard/Components/MerchantSummary/Merchant"),
);
const Skusummary = lazy(
  () => import("./screens/PMDashboard/Components/SKUSummary/Skusummary"),
);
const VirtualAccounts = lazy(
  () => import("./screens/VirtualAccounts/VirtualAccounts"),
);
const LoanDetails = lazy(() => import("./screens/LoanDetails/LoanDetails"));
const PmVerifySkuLoanDetails = lazy(
  () => import("./screens/PmVerifySkuLoanDetails/PmVerifySkuLoanDetails"),
);
const Repayment = lazy(() => import("./screens/PMRepayment/Repayment"));
const IssueNote = lazy(() => import("./screens/IssueSNOverview/IssueNote"));
const SkuLoanApplication = lazy(
  () => import("./screens/SkuLoanApplication/SkuLoanApplication"),
);
const Withdraw = lazy(() => import("./screens/Withdraw/Withdraw"));
const FeatureFlag = lazy(() => import("./screens/FeatureFlag/FeatureFlag"));
const ExternalAccounts = lazy(
  () => import("./screens/ExternalAccounts/ExternalAccounts"),
);
const Audit = lazy(() => import("./screens/Audit/Audit"));
const Inventory = lazy(
  () => import("./screens/InventoryOrders/Inventory/Inventory"),
);
const Orders = lazy(() => import("./screens/InventoryOrders/Orders/Orders"));
const FundPull = lazy(
  () => import("./screens/InventoryOrders/FundPull/FundPull"),
);
const Onboarding = lazy(() => import("./screens/SalesOnboarding/Onboarding"));
const OnboardingFormWrapper = lazy(
  () => import("./screens/SalesOnboarding/OnboardingFormWrapper"),
);
const FundPullAutoRefund = lazy(
  () => import("./screens/MerchantConfiguration/FundPullAutoRefund"),
);
const Scheme = lazy(() => import("./screens/Scheme/Scheme"));

type LayoutType = "product" | "operationAndRisk" | "salesSupport";

interface PrivateRouteConfig {
  path: string;
  component: React.LazyExoticComponent<React.ComponentType>;
  layout: LayoutType;
}

const layoutMap: Record<
  LayoutType,
  React.ComponentType<{ content: React.ReactNode }>
> = {
  product: ProductLayout,
  operationAndRisk: OperationAndRiskLayout,
  salesSupport: SalesSupportLayout,
};

const privateRoutes: PrivateRouteConfig[] = [
  // Product layout routes
  { path: "/pm-dashboard/:subpath", component: Dashboard, layout: "product" },
  { path: "/withdrawal", component: Withdraw, layout: "product" },
  { path: "/investorsummary", component: InvestorSummary, layout: "product" },
  { path: "/merchant", component: Merchant, layout: "product" },
  { path: "/snusummary", component: Skusummary, layout: "product" },
  { path: "/repayment/:id", component: Repayment, layout: "product" },
  { path: "/note-list", component: Notelist, layout: "product" },
  { path: "/create-campaign", component: CreateCampaign, layout: "product" },
  { path: "/campaign-list", component: CampaignList, layout: "product" },
  { path: "/loan-details", component: LoanDetails, layout: "product" },
  { path: "/verify-sku-loans", component: PmVerifySkuLoans, layout: "product" },
  {
    path: "/verify-sku-loan-details",
    component: PmVerifySkuLoanDetails,
    layout: "product",
  },
  { path: "/issue-note/:id", component: IssueNote, layout: "product" },
  { path: "/issuer", component: Issusingspv, layout: "product" },
  {
    path: "/structured-note/:tabValue",
    component: StructuredNotePage,
    layout: "product",
  },
  { path: "/virtual-accounts", component: VirtualAccounts, layout: "product" },
  {
    path: "/external-accounts",
    component: ExternalAccounts,
    layout: "product",
  },
  { path: "/audit", component: Audit, layout: "product" },
  { path: "/feature-flags", component: FeatureFlag, layout: "product" },
  { path: "/inventory", component: Inventory, layout: "product" },
  { path: "/orders", component: Orders, layout: "product" },
  { path: "/fund-pull", component: FundPull, layout: "product" },
  {
    path: "/fund-pull-auto-refund",
    component: FundPullAutoRefund,
    layout: "product",
  },
  { path: "/schemes", component: Scheme, layout: "product" },

  // Operation & Risk layout routes
  { path: "/org-list", component: OrgDetailsPage, layout: "operationAndRisk" },
  {
    path: "/individual-list",
    component: IndividualList,
    layout: "operationAndRisk",
  },

  // Sales Support layout routes
  {
    path: "/sales-support/:tabValue",
    component: SkuLoanApplication,
    layout: "salesSupport",
  },
  { path: "/onboarding", component: Onboarding, layout: "salesSupport" },
  {
    path: "/onboardingForm",
    component: OnboardingFormWrapper,
    layout: "salesSupport",
  },
];

const SuspenseFallback = (
  <Center style={{ height: "100vh" }}>
    <Loader size="lg" />
  </Center>
);

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const token = useSelector((state: RootState) => state.auth.token);

  return !token || token === "none" ? (
    <Navigate to="/login" replace />
  ) : (
    <>{children}</>
  );
};

function App() {
  return (
    <QueryClientProvider client={client}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ToastContainer />
          <BrowserRouter>
            <SessionInterceptorProvider
              persistor={persistor}
              resetActions={adminResetActions}
              tokenSelector={(state: RootState) => state.auth}
            >
              <JwtProvider
                tokenSelector={(state: RootState) => state.auth}
                setTokenAction={setToken}
                resetActions={adminResetActions}
              >
                <Suspense fallback={SuspenseFallback}>
                  <Routes>
                    {/* Public routes */}
                    <Route
                      path="/login"
                      element={<LoginLayout content={<Login />} />}
                    />
                    <Route
                      path="/two-auth-QR-code"
                      element={<Navbar content={<TwoFAQRPageCommon />} />}
                    />
                    <Route
                      path="/password-reset"
                      element={
                        <AcknowledgementLayout content={<PasswordReset />} />
                      }
                    />
                    <Route
                      path="/reset-message"
                      element={
                        <AcknowledgementLayout content={<PasswordResetMsg />} />
                      }
                    />

                    {/* Private routes */}
                    {privateRoutes.map(
                      ({ path, component: Screen, layout }) => {
                        const Layout = layoutMap[layout];
                        return (
                          <Route
                            key={path}
                            path={path}
                            element={
                              <PrivateRoute>
                                <Layout content={<Screen />} />
                              </PrivateRoute>
                            }
                          />
                        );
                      },
                    )}

                    {/* Catch-all redirect */}
                    <Route
                      path="*"
                      element={<Navigate to="/login" replace />}
                    />
                  </Routes>
                </Suspense>
              </JwtProvider>
            </SessionInterceptorProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
